/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";

import { useUser } from "@/hooks/useUser";
import useIsMobile from "@/hooks/useIsMobile";
import { prependURL, getFileUri } from "@/utils/helpers";
import BoxCard from "@/components/common/BoxCard";
import Table from "@/components/common/Table";
import ParsedHTML from "@/components/common/ParsedHTML";

const AccreditationRegulations = ({
  rounded = true,
}: {
  rounded?: boolean;
}): JSX.Element => {
  const [session, loading] = useUser();
  const lang = session?.user?.profile?.locale;
  const { t } = useTranslation("common");
  const isMobile = useIsMobile();

  const {
    isOpen: isAgreementOpen,
    onOpen: onAgreementOpen,
    onClose: onAgreementClose,
  } = useDisclosure();
  const [agreementHTML, setAgreementHTML] = useState("");

  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext();

  const accreditationAgreements = watch("event.accreditationAgreements");
  const agreements = useMemo(
    () =>
      lang &&
      accreditationAgreements?.filter(
        (agreement) => agreement?.required?.[lang]
      ),
    [lang, accreditationAgreements]
  );

  const columns = useMemo(
    () => [
      {
        Header: t("events.document"),
        accessor: (regulation) => regulation?.name,
        Cell: function Document({ row }: { row: any }) {
          return (
            <Flex>
              <FormControl
                isInvalid={!!errors[`agreement_${row?.original?.id}`]}
                isRequired
              >
                {row?.original?.regulation?.content ? (
                  <Link
                    onClick={() => {
                      setAgreementHTML(row?.original?.regulation?.content);
                      onAgreementOpen();
                    }}
                  >
                    {row?.original?.regulation?.name}
                  </Link>
                ) : row?.original?.regulation?.url ? (
                  <Link
                    target="_blank"
                    href={prependURL(row?.original?.regulation?.url)}
                    rel="noopener noreferrer"
                  >
                    {row?.original?.regulation?.name}
                  </Link>
                ) : row?.original?.regulation?.file ? (
                  <Link
                    target="_blank"
                    href={getFileUri(row?.original?.regulation?.file?.path)}
                    rel="noopener noreferrer"
                  >
                    {row?.original?.regulation?.name}
                  </Link>
                ) : null}
                {errors[`agreement_${row?.original?.id}`] && (
                  <FormErrorMessage>
                    {errors[`agreement_${row?.original?.id}`].message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <Switch
                {...register(`agreement_${row?.original?.id}`, {
                  required: {
                    value: true,
                    message: t("events.documentIsRequired"),
                  },
                })}
              />
            </Flex>
          );
        },
      },
    ],
    [errors]
  );

  const renderRegulation = (regulation) => (
    <Flex
      key={regulation.id}
      justifyContent="space-between"
      alignItems="center"
      py={2}
      w="100%"
    >
      <FormControl
        isInvalid={!!errors[`agreement_${regulation?.id}`]}
        isRequired
        pr={4}
      >
        {regulation?.regulation?.content ? (
          <Link
            onClick={() => {
              setAgreementHTML(regulation?.regulation?.content);
              onAgreementOpen();
            }}
          >
            {regulation?.regulation?.name}
          </Link>
        ) : regulation?.regulation?.url ? (
          <Link
            target="_blank"
            href={prependURL(regulation?.regulation?.url)}
            rel="noopener noreferrer"
          >
            {regulation?.regulation?.name}
          </Link>
        ) : regulation?.regulation?.file ? (
          <Link
            target="_blank"
            href={getFileUri(regulation?.regulation?.file?.path)}
            rel="noopener noreferrer"
          >
            {regulation?.regulation?.name}
          </Link>
        ) : null}
        {errors[`agreement_${regulation?.id}`] && (
          <FormErrorMessage>
            {errors[`agreement_${regulation?.id}`].message}
          </FormErrorMessage>
        )}
      </FormControl>

      <Switch
        {...register(`agreement_${regulation?.id}`, {
          required: {
            value: true,
            message: t("events.documentIsRequired"),
          },
        })}
      />
    </Flex>
  );

  if (loading || agreements?.length === 0) return null;

  return (
    <>
      <BoxCard p={0} mt={3} borderRadius={rounded ? ".375rem" : 0}>
        <Box p="1.25rem" borderBottomColor="light.50" borderBottomWidth="1px">
          <Heading as="h3" size="md">
            {t("events.terms")}
          </Heading>
        </Box>

        <Box p="1.25rem">
          {isMobile ? (
            <VStack spacing={4} align="stretch">
              {agreements?.map((agreement: any) => renderRegulation(agreement))}
            </VStack>
          ) : (
            <Table
              columns={columns}
              data={agreements || []}
              canFilter={false}
              searchBar={false}
              pagination={false}
            />
          )}
        </Box>
      </BoxCard>

      {isAgreementOpen && (
        <Modal
          isOpen
          onClose={onAgreementClose}
          size="6xl"
          preserveScrollBarGap
          isCentered
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("events.document")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <ParsedHTML html={agreementHTML} />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" onClick={onAgreementClose}>
                {t("close")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default AccreditationRegulations;
